import React from "react";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import { X, Save, Trash2, FileText } from "lucide-react";

const AppointmentModal = ({
  isOpen,
  onClose,
  form,
  setForm,
  onSave,
  onDelete,
  isEdit,
  waybillMissionId,
  onDownloadWaybill,
}) => {
  const { t } = useTranslation();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="w-[97vw] max-w-lg rounded-2xl p-0 shadow-2xl mx-auto relative animate-fade-in bg-base-100 border border-base-200"
      overlayClassName="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      ariaHideApp={false}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-base-200">
        <h2 className="text-lg font-bold text-base-content">
          {isEdit ? `✏️ ${t("calendar.edit")}` : `+ ${t("calendar.new")}`}
        </h2>
        <button
          onClick={onClose}
          className="btn btn-ghost btn-sm btn-square text-base-content/40"
          title={t("calendar.cancel")}
        >
          <X size={18} />
        </button>
      </div>

      {/* Form */}
      <form
        onSubmit={e => { e.preventDefault(); onSave(); }}
        className="p-6 space-y-4"
      >
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            👤 {t("calendar.client")}
          </label>
          <input
            name="client"
            placeholder={t("calendar.client")}
            value={form.client}
            onChange={handleChange}
            className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
            autoFocus
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            📍 {t("calendar.address")}
          </label>
          <input
            name="address"
            placeholder={t("calendar.address")}
            value={form.address}
            onChange={handleChange}
            className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            📞 {t("calendar.phone")}
          </label>
          <input
            name="phone"
            placeholder={t("calendar.phone")}
            value={form.phone}
            onChange={handleChange}
            className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            ✉️ {t("calendar.email", "Email")}
          </label>
          <input
            name="email"
            type="email"
            placeholder={t("calendar.emailPlaceholder", "Email du client (pour confirmation + rappel)")}
            value={form.email || ""}
            onChange={handleChange}
            className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            📝 Note
          </label>
          <textarea
            id="note"
            name="note"
            placeholder="Note ou commentaire sur le rendez-vous"
            value={form.note || ""}
            onChange={handleChange}
            className="textarea textarea-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none min-h-[70px] resize-vertical"
            rows={3}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-base-content/70">
              🚚 {t("calendar.type")}
            </label>
            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              className="select select-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
            >
              <option value="Moving">🚚 {t("calendar.moving")}</option>
              <option value="Delivery">📦 {t("calendar.delivery")}</option>
              <option value="Visite">👁 {t("calendar.visit", "Visite")}</option>
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-base-content/70">
              📅 {t("calendar.date")}
            </label>
            <input
              name="date"
              type="date"
              value={form.date}
              onChange={handleChange}
              className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            🕐 {t("calendar.time")}
          </label>
          <input
            name="time"
            type="time"
            value={form.time}
            onChange={handleChange}
            className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-2 rounded-lg bg-base-200 px-3 py-2 border border-base-300">
          <input
            id="appt-confirmed"
            type="checkbox"
            checked={form.status === "confirmed"}
            onChange={(e) =>
              setForm({ ...form, status: e.target.checked ? "confirmed" : "pending" })
            }
            className="checkbox checkbox-sm checkbox-primary"
          />
          <label htmlFor="appt-confirmed" className="text-sm text-base-content/80 cursor-pointer">
            {t("calendar.notifyClient", "Confirmer et notifier le client (email si renseigné)")}
          </label>
        </div>

        {/* LDV récupérable seulement si un mission liée (même quote_id) existe déjà. */}
        {isEdit && waybillMissionId && (
          <button
            type="button"
            onClick={() => onDownloadWaybill(waybillMissionId)}
            className="btn btn-outline btn-sm w-full flex items-center justify-center gap-1"
          >
            <FileText size={15} /> {t("mission.list.waybill")}
          </button>
        )}

        {/* Footer actions */}
        <div className="flex justify-between items-center pt-2 gap-2">
          {isEdit && (
            <button
              type="button"
              onClick={onDelete}
              className="btn btn-ghost btn-sm text-error hover:bg-error/10 flex items-center gap-1"
            >
              <Trash2 size={15} /> {t("calendar.delete")}
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost btn-sm"
            >
              <X size={15} /> {t("calendar.cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm flex items-center gap-1"
            >
              <Save size={15} /> {t("calendar.save")}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AppointmentModal;
